import { Box, Stack, Typography } from "@mui/material"

import { ChangelogStatus } from "@models/changelog"

// Status badge palette — soft, harmonized tints (same lightness weight) so the
// column reads clean and consistent; a colored dot carries the meaning.
export function statusMeta(status: ChangelogStatus): {
  key: string
  color: string
  bg: string
} {
  switch (status) {
    case ChangelogStatus.PUBLISHED:
      return {
        key: "changelog_status_published",
        color: "#3F8F5B",
        bg: "#F1F6F2"
      }
    case ChangelogStatus.SCHEDULED:
      return {
        key: "changelog_status_scheduled",
        color: "#B08641",
        bg: "#F7F3EC"
      }
    case ChangelogStatus.ARCHIVED:
      return {
        key: "changelog_status_archived",
        color: "#7A8494",
        bg: "#F3F4F6"
      }
    case ChangelogStatus.DRAFT:
    default:
      return { key: "changelog_status_draft", color: "#7A8494", bg: "#F3F4F6" }
  }
}

// Reaction average → emoji (backend numeric, frontend emoji per PRD).
export function reactionEmoji(avg: number): string {
  if (avg >= 4.5) {
    return "😍"
  }
  if (avg >= 3.8) {
    return "🙂"
  }
  if (avg >= 3) {
    return "😐"
  }
  if (avg >= 2) {
    return "😕"
  }
  return "😞"
}

export function langFlag(lang: string): string {
  const map: Record<string, string> = {
    tr: "🇹🇷",
    en: "🇬🇧",
    de: "🇩🇪",
    fr: "🇫🇷",
    ar: "🇸🇦"
  }
  return map[lang] || "🏳️"
}

export function langLabel(lang: string): string {
  const map: Record<string, string> = {
    tr: "Türkçe",
    en: "English",
    de: "Deutsch",
    fr: "Français",
    ar: "العربية"
  }
  return map[lang] || lang.toUpperCase()
}

const MONTHS_TR = [
  "Oca",
  "Şub",
  "Mar",
  "Nis",
  "May",
  "Haz",
  "Tem",
  "Ağu",
  "Eyl",
  "Eki",
  "Kas",
  "Ara"
]

export function formatShortDate(value?: string | null): string {
  if (!value) {
    return "—"
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) {
    return "—"
  }
  return `${d.getDate()} ${MONTHS_TR[d.getMonth()]} ${d.getFullYear()}`
}

// Two-line section header with title + subtitle + right-aligned actions.
export function SectionHeader(props: {
  title: string
  subtitle?: string
  right?: React.ReactNode
}) {
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      sx={{ mb: 2 }}
    >
      <Box>
        <Typography sx={{ fontSize: "1.4rem", fontWeight: 700 }}>
          {props.title}
        </Typography>
        {props.subtitle && (
          <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
            {props.subtitle}
          </Typography>
        )}
      </Box>
      {props.right && (
        <Stack direction="row" gap={1.5} alignItems="center">
          {props.right}
        </Stack>
      )}
    </Stack>
  )
}
